import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, CameraOff, Keyboard, PackageCheck, Loader2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Input } from "@/components/ui/input";

const REGION = "lm-qr-region";

export default function Scan() {
  const [cam, setCam] = useState(false);
  const [manual, setManual] = useState("");
  const [waiver, setWaiver] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const scanner = useRef(null);

  const submit = async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const r = await api.post("/returns/scan", {
        asset_code: code,
        waiver_reason: waiver || undefined, source: cam ? "camera" : "manual",
      });
      setResult(r.data);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan gagal");
    } finally { setBusy(false); }
  };

  const startCam = async () => {
    setCam(true);
    try {
      const inst = new Html5Qrcode(REGION);
      scanner.current = inst;
      await inst.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
        async (text) => {
          try {
            await inst.pause(true);
          } catch (error) {
            logError("Gagal menjeda kamera setelah QR terbaca", error);
          }
          await submit(text);
          window.setTimeout(() => inst.resume().catch((error) => {
            logError("Gagal melanjutkan kamera", error);
          }), 1500);
        },
        () => {});
    } catch (e) {
      setCam(false);
      toast.error("Kamera tidak bisa dibuka. Pakai input manual di bawah.");
    }
  };

  const stopCam = async () => {
    try {
      await scanner.current?.stop();
    } catch (error) {
      logError("Kamera sudah tidak aktif", error);
    }
    scanner.current = null;
    setCam(false);
  };

  return (
    <div data-testid="admin-scan" className="pb-24">
      <h1 className="font-display text-3xl tracking-tight">Scan Resi Pengembalian</h1>
      <p className="mt-1 text-sm text-ink-soft max-w-xl">
        Setelah semua meja diperiksa secara fisik, scan QR pada resi order. Satu scan menandai
        seluruh unit pada order sudah diterima dan mengirim tugas refund ke Finance.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {!cam ? (
          <button data-testid="start-camera-btn" onClick={startCam}
            className="lm-btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">
            <Camera size={15} /> Nyalakan kamera
          </button>
        ) : (
          <button data-testid="stop-camera-btn" onClick={stopCam}
            className="px-6 py-3 rounded-full border border-black/15 text-sm inline-flex items-center gap-2">
            <CameraOff size={15} /> Matikan kamera
          </button>
        )}
      </div>

      <div id={REGION} data-testid="qr-region"
        className={`mt-4 rounded-xl overflow-hidden bg-black/90 ${cam ? "block" : "hidden"}`} />

      <div className="mt-6 bg-white border border-black/10 rounded-lg p-5">
        <div className="text-sm font-semibold inline-flex items-center gap-2">
          <Keyboard size={15} /> Input kode resi
        </div>
        <div className="mt-3">
          <Input data-testid="manual-receipt-input" className="h-12" placeholder="Kode di QR resi"
            value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} />
        </div>
        <Input data-testid="waiver-input" className="h-12 mt-3"
          placeholder="Alasan kalau scan di luar jam operasional Hub"
          value={waiver} onChange={(e) => setWaiver(e.target.value)} />
        <button data-testid="manual-scan-btn" disabled={busy || !manual} onClick={() => submit(manual)}
          className="mt-3 lm-btn-primary rounded-md px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} Catat kembali
        </button>
      </div>

      {result && (
        <div className="mt-6 bg-white border border-black/10 rounded-lg p-5" data-testid="scan-result">
          <div className="text-sm">
            <b data-testid="scan-booking-code">{result.booking_code}</b> ·{" "}
            {result.units_returned}/{result.units_total} meja kembali
          </div>
          <div data-testid="finance-refund-notice" className="mt-4 border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
            Unit telah dicatat diterima. Finance sudah mendapat notifikasi untuk memproses refund deposit.
          </div>
        </div>
      )}
    </div>
  );
}
